import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { findDuplicates, formatMoney } from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { addTransaction } from '../db/repository.js';
import { Sheet } from '../components/primitives.js';
import { QuickEntryForm, type QuickEntryValues } from '../components/QuickEntryForm.js';
import { createTransaction } from '@wis/shared';

export interface QuickAddSheetProps {
  open: boolean;
  onClose: () => void;
  initial?: Partial<QuickEntryValues>;
  source?: 'manual' | 'apple-pay-shortcut' | 'maybank-quick-capture' | 'tng-quick-capture';
  title?: string;
}

/**
 * The add sheet.
 *
 * The only thing it does beyond saving is warn about a likely duplicate -- and
 * it warns rather than blocks, because two identical payments minutes apart is
 * a real thing that happens and the app does not get to overrule the person
 * who was standing at the counter.
 */
export function QuickAddSheet({
  open,
  onClose,
  initial,
  source = 'manual',
  title = 'Add a payment',
}: QuickAddSheetProps): JSX.Element {
  const { categories, accounts, rules, merchantHistory, settings, transactions, toast } = useApp();

  const handleSubmit = useCallback(async (values: QuickEntryValues): Promise<void> => {
    const candidate = createTransaction({
      ...values,
      source,
      currency: settings.currency,
    });

    const duplicates = findDuplicates(candidate, transactions);
    if (duplicates.length > 0) {
      const first = duplicates[0];
      const confirmed = window.confirm(
        `This looks like a payment you already have: ${formatMoney(first?.transaction.amountSen ?? 0)} at ${
          first?.transaction.merchantNormalized || 'no merchant'
        }, ${first?.minutesApart} minute(s) apart.\n\nAdd it anyway?`,
      );
      if (!confirmed) {
        onClose();
        return;
      }
    }

    await addTransaction({ ...values, source, currency: settings.currency });
    toast(`Saved ${formatMoney(values.amountSen, { currency: settings.currency })}`);
    onClose();
  }, [onClose, settings.currency, source, toast, transactions]);

  return (
    <Sheet open={open} title={title} onClose={onClose}>
      <QuickEntryForm
        categories={categories}
        accounts={accounts}
        rules={rules}
        history={merchantHistory}
        timeZone={settings.timeZone}
        defaultAccountLabel={settings.defaultAccountLabel}
        defaultCategoryId={settings.defaultCategoryId}
        currency={settings.currency}
        {...(initial ? { initial } : {})}
        onSubmit={handleSubmit}
        onCancel={onClose}
      />
    </Sheet>
  );
}

/** The full-page version, used by the `/add` home-screen shortcut. */
export function QuickAddPage(): JSX.Element {
  const navigate = useNavigate();
  return (
    <QuickAddSheet
      open
      onClose={() => navigate('/', { replace: true })}
      title="Add a payment"
    />
  );
}
